import { Users, Heart, MonitorX, Leaf, Activity, Mountain } from 'lucide-react';

export const ValuesSection = () => {
  return (
    <section id="values" className="py-24 bg-card">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground leading-tight">
            The Path We Follow
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-8"></div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Our approach grows from global trends and timeless values that are reshaping how we live, rest, and connect.
          </p>
        </div>

        <div className="space-y-16">
          {/* First Row - Digital Reset & Return to Nature */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex items-start gap-6 group">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                <MonitorX className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="text-2xl font-medium mb-4 text-foreground">Digital Reset</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A growing need to disconnect from screens and constant stimulation – and reconnect with presence, silence, and real human connection.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-6 group md:justify-end">
              <div className="md:order-2 w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                <Leaf className="w-8 h-8 text-accent" />
              </div>
              <div className="md:order-1 md:text-right">
                <h3 className="text-2xl font-medium mb-4 text-foreground">Return to Nature</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A shift toward spending time in forests, by the water, and under open skies – places that remind us to slow down and just be.
                </p>
              </div>
            </div>
          </div>

          {/* Second Row - Slow Life & Meaningful Connections */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex items-start gap-6 group">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                <Heart className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="text-2xl font-medium mb-4 text-foreground">Slow Life</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A move away from hustle culture toward simplicity, intention, and everyday rituals that nourish the soul.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-6 group md:justify-end">
              <div className="md:order-2 w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                <Users className="w-8 h-8 text-accent" />
              </div>
              <div className="md:order-1 md:text-right">
                <h3 className="text-2xl font-medium mb-4 text-foreground">Meaningful Connections</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A longing for deeper relationships – with ourselves, our loved ones, and the world around us.
                </p>
              </div>
            </div>
          </div>

          {/* Third Row - Mind–Body Balance & Unplugged Tourism */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex items-start gap-6 group">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                <Activity className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="text-2xl font-medium mb-4 text-foreground">Mind–Body Balance</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A renewed focus on inner clarity and physical wellbeing – through movement, breath, rest, and natural care that brings us back to harmony.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-6 group md:justify-end">
              <div className="md:order-2 w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                <Mountain className="w-8 h-8 text-accent" />
              </div>
              <div className="md:order-1 md:text-right">
                <h3 className="text-2xl font-medium mb-4 text-foreground">Unplugged Tourism</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A new way of traveling that values presence over performance – where nature, simplicity, and analog experiences take center stage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};